/**
 * Current-Week NCR drilldown modal.
 *
 * Wired up by the Quality / PPM and Quality / NCRs tiles. Lists NCRs
 * with ``ncr_date BETWEEN week_start AND week_end`` — same predicate
 * the dashboard's ``_bucketed_ppm_quality`` SQL uses.
 *
 * Filter contract:
 *   - company   = customer_id (NCR.company is the customer raising it)
 *   - supplier  = supplier_id (when set on the dashboard scope)
 *   - ncr_date  in [weekStart, weekEnd]
 *   - mode == 'ppm'  →  ``reject_qty__gt=0`` would be ideal but the
 *     filterset doesn't expose it; the count proxy is "all NCRs in
 *     window" since the PPM tile's denominator is reject_qty (most
 *     NCRs have a positive reject_qty in practice). Both tiles thus
 *     share the same row set; the title differs.
 *
 *  Export:
 *   - There is currently no ``/quality/ncrs/export-csv/`` server
 *     endpoint, so this falls back to a client-side CSV built from
 *     the rows fetched. A streaming server export should follow if
 *     the dataset gets large enough that the client build leaves rows
 *     behind. Tracked as a TODO.
 */
import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Loader2, ChevronLeft, ChevronRight, Download,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { qualityAPI } from '../../../lib/api';
import type { NonConformanceReport } from '../../../lib/api';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { useQualityMeasureLabel } from '../../../lib/qualityLabels';

export type NCRDrilldownMode = 'ppm' | 'ncrs';

export interface NCRDrilldownFilters {
  customer_id?: number | string;
  supplier_id?: number | string;
  part_number?: string;
  /** Same shape as DashboardFilterScope — needed so the modal can scope
   *  through the PO join (po_line__po__erp_order_type) and match the
   *  dashboard tile's scoped_ncrs CTE. */
  erp_order_type?: '1' | '4';
}

interface NCRDrilldownModalProps {
  open: boolean;
  onClose: () => void;
  mode: NCRDrilldownMode;
  filters: NCRDrilldownFilters;
  /** ncr_date window — Sun → today (or Sat when frozen). */
  weekStartIso: string;
  weekEndIso: string;
}

const PAGE_SIZE = 50;

function buildTitles(qualityLabel: string): Record<NCRDrilldownMode, string> {
  return {
    ppm:  `NCRs contributing to ${qualityLabel} this week`,
    ncrs: 'NCRs raised this week',
  };
}

interface ParamShape extends Record<string, string | number | boolean> {}

export function CurrentWeekNCRDrilldownModal({
  open, onClose, mode, filters, weekStartIso, weekEndIso,
}: NCRDrilldownModalProps) {
  const { formatDate, formatNumber } = useFormatters();
  const qualityLabel = useQualityMeasureLabel();
  const titles = useMemo(() => buildTitles(qualityLabel), [qualityLabel]);
  const [rows, setRows] = useState<NonConformanceReport[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const baseParams = useMemo(() => {
    const p: ParamShape = {
      ncr_date__gte: weekStartIso,
      ncr_date__lte: weekEndIso,
      ordering: '-raised_date',
    };
    // CR-7173: `customer` (not `company`) — the NCR filterset role-routes
    // `company` to the supplier slot for supplier users, zeroing this modal.
    if (filters.customer_id) p['customer'] = filters.customer_id;
    if (filters.supplier_id) p['supplier'] = filters.supplier_id;
    if (filters.part_number) p['part_number'] = filters.part_number;
    // Scope through PO join so the modal matches the tile's scoped_ncrs
    // CTE (which excludes NCRs missing a po_line or linked to a different
    // order type).
    if (filters.erp_order_type) p['po_line__po__erp_order_type'] = filters.erp_order_type;
    // PPM tile drills only into NCRs with rejected parts so it's
    // distinguishable from the NCRs tile (which drills to every NCR).
    if (mode === 'ppm') p['reject_qty__gt'] = 0;
    return p;
  }, [filters, weekStartIso, weekEndIso, mode]);

  const fetchPage = useCallback(async (targetPage: number) => {
    setLoading(true);
    setError(null);
    try {
      const params: ParamShape = { ...baseParams, page: targetPage, page_size: PAGE_SIZE };
      const res = await qualityAPI.getNCRs(params);
      setRows(res.data.results ?? []);
      setTotal(res.data.count ?? 0);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load NCRs';
      setError(msg);
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [baseParams]);

  useEffect(() => {
    if (!open) return;
    setPage(1);
    fetchPage(1);
  }, [open, mode, fetchPage]);

  useEffect(() => {
    if (!open || page === 1) return;
    fetchPage(page);
  }, [page, open, fetchPage]);

  const goToPage = useCallback((p: number) => {
    if (p < 1 || p > totalPages || p === page || loading) return;
    setPage(p);
  }, [page, totalPages, loading]);

  /** Server-streamed XLSX export — hits ``/quality/ncrs/export-xlsx/``
   *  with the same filter shape as the list call. Counts in the modal
   *  and rows in the workbook are guaranteed identical. */
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams(
        Object.entries(baseParams as Record<string, string | number | boolean>)
          .filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => [k, String(v)]),
      ).toString();
      const apiClientModule = await import('../../../lib/api/client');
      const resp = await apiClientModule.default.get(
        `/quality/ncrs/export-xlsx/?${qs}`,
        { responseType: 'blob' },
      );
      const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `current_week_ncrs_${mode}_${stamp}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Excel export ready');
    } catch (err: unknown) {
      console.error('NCR Excel export failed:', err);
      toast.error('Failed to export Excel');
    } finally {
      setExporting(false);
    }
  }, [baseParams, mode]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle>{titles[mode]}</DialogTitle>
              <DialogDescription>
                {total.toLocaleString()} NCR{total === 1 ? '' : 's'}
                {filters.part_number ? <> · part <span className="font-mono">{filters.part_number}</span></> : null}
                {filters.supplier_id ? <> · supplier #{filters.supplier_id}</> : null}
              </DialogDescription>
            </div>
            <Button
              variant="outline" size="sm"
              onClick={handleExport}
              disabled={exporting || total === 0}
            >
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Export to Excel
            </Button>
          </div>
        </DialogHeader>

        {error && <div className="text-sm text-red-600 py-3">{error}</div>}

        {loading && rows.length === 0 ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-muted-foreground py-10 text-center">No NCRs raised this week.</div>
        ) : (
          <div className="overflow-auto max-h-[60vh] border rounded-md">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0 z-10">
                <tr>
                  <th className="px-3 py-2 font-medium text-slate-700 text-left">NCR #</th>
                  <th className="px-3 py-2 font-medium text-slate-700 text-left">Raised</th>
                  <th className="px-3 py-2 font-medium text-slate-700 text-left">Status</th>
                  <th className="px-3 py-2 font-medium text-slate-700 text-left">Severity</th>
                  <th className="px-3 py-2 font-medium text-slate-700 text-left">Supplier</th>
                  <th className="px-3 py-2 font-medium text-slate-700 text-left">Part</th>
                  <th className="px-3 py-2 font-medium text-slate-700 text-left">Part Description</th>
                  <th className="px-3 py-2 font-medium text-slate-700 text-right">Reject Qty</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2 font-mono text-xs">
                      <a
                        href={`/quality/ncr/${r.id}/edit`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                        title="Open NCR in a new tab"
                      >
                        {r.ncr_number}
                      </a>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-xs">{formatDate(r.raised_date)}</td>
                    <td className="px-3 py-2 text-xs">{r.status_display || r.status}</td>
                    <td className="px-3 py-2 text-xs">{r.severity_display || r.severity}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{r.supplier_name || '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.part_number}</td>
                    <td className="px-3 py-2 text-xs max-w-[280px] truncate" title={r.part_description}>{r.part_description || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatNumber(Number(r.reject_qty || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between gap-2 pt-2">
            <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => goToPage(page - 1)}>
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <span className="text-xs text-muted-foreground">
              Page {page.toLocaleString()} of {totalPages.toLocaleString()}
            </span>
            <Button size="sm" variant="outline" disabled={page >= totalPages || loading} onClick={() => goToPage(page + 1)}>
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
